import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Loader2,
  Settings,
  Building2,
  CreditCard,
  Percent,
  Calendar,
  ShieldCheck,
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import { doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  query,
  collection,
  where,
  orderBy,
  onSnapshot } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';

interface PayrollSettings {
  companyName: string;
  taxNumber: string;
  payFrequency: 'Monthly' | 'Weekly' | 'Bi-weekly';
  payDay: number;
  currency: string;
  salaryAccount?: string;
  liabilityAccount?: string;
  deductions: { name: string; percentage: number }[];
  allowances: { name: string; percentage: number }[];
}

type PayrollItem = { name: string; percentage: number };

const mapPayrollItems = (value: unknown, fallback: PayrollItem[]): PayrollItem[] =>
  Array.isArray(value)
    ? value.map((item: any) => ({
        name: typeof item?.name === 'string' ? item.name : '',
        percentage: typeof item?.percentage === 'number' ? item.percentage : 0,
      }))
    : fallback;

const payrollFromJson = (payroll: any, fallback: PayrollSettings): PayrollSettings => ({
  companyName: typeof payroll?.companyName === 'string' ? payroll.companyName : fallback.companyName,
  taxNumber: typeof payroll?.taxNumber === 'string' ? payroll.taxNumber : fallback.taxNumber,
  payFrequency: ['Monthly', 'Weekly', 'Bi-weekly'].includes(payroll?.payFrequency)
    ? payroll.payFrequency
    : fallback.payFrequency,
  payDay: typeof payroll?.payDay === 'number' ? payroll.payDay : fallback.payDay,
  currency: typeof payroll?.currency === 'string' ? payroll.currency : fallback.currency,
  salaryAccount: typeof payroll?.salaryAccount === 'string' ? payroll.salaryAccount : fallback.salaryAccount,
  liabilityAccount: typeof payroll?.liabilityAccount === 'string' ? payroll.liabilityAccount : fallback.liabilityAccount,
  deductions: mapPayrollItems(payroll?.deductions, fallback.deductions),
  allowances: mapPayrollItems(payroll?.allowances, fallback.allowances),
});

const payrollToJson = (settings: PayrollSettings) => ({
  companyName: settings.companyName,
  taxNumber: settings.taxNumber,
  payFrequency: settings.payFrequency,
  payDay: settings.payDay,
  currency: settings.currency,
  salaryAccount: settings.salaryAccount,
  liabilityAccount: settings.liabilityAccount,
  deductions: settings.deductions.map(({ name, percentage }) => ({ name, percentage })),
  allowances: settings.allowances.map(({ name, percentage }) => ({ name, percentage })),
});

export default function PayrollSettingsView({ orgId, orgSettings, language = 'en' }: { orgId: string, orgSettings?: any, language?: string }) {
  const [settings, setSettings] = useState<PayrollSettings>({
    companyName: '',
    taxNumber: '',
    payFrequency: 'Monthly',
    payDay: 25,
    currency: getDefaultCurrency(orgSettings),
    deductions: [],
    allowances: []
  });

  useEffect(() => {
    if (orgSettings?.defaultCurrency && !settings.currency) {
      setSettings(prev => ({ ...prev, currency: getDefaultCurrency(orgSettings) }));
    }
  }, [orgSettings?.defaultCurrency]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [accountsList, setAccountsList] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;
    const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    if (localToken) {
      let cancelled = false;
      fetch(`/api/organizations/${encodeURIComponent(orgId)}/chart-of-accounts`, {
        headers: { Authorization: `Bearer ${localToken}` }
      })
        .then(r => r.json())
        .then(data => { if (!cancelled) setAccountsList(Array.isArray(data) ? data : []); })
        .catch(err => { if (!cancelled) console.error('COA load failed:', err); });
      return () => { cancelled = true; };
    }
    const q = query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId), orderBy('code', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAccountsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
    });
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    const fetchSettings = async () => {
      const localAuthToken = typeof window !== 'undefined'
        ? localStorage.getItem('app_auth_token')
        : null;

      try {
        if (localAuthToken) {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/settings`, {
            headers: { Authorization: `Bearer ${localAuthToken}` },
          });
          if (!response.ok) {
            throw new Error(`Payroll settings request failed: ${response.status}`);
          }

          const organizationSettings = await response.json();
          setSettings(currentSettings => payrollFromJson(organizationSettings?.payroll, currentSettings));
          return;
        }

        const docRef = doc(db, 'organizations', orgId, 'settings', 'payroll');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(docSnap.data() as PayrollSettings);
        }
      } catch (error) {
        console.error("Error fetching payroll settings:", error);
        notify.error("Failed to load settings");
        if (!localAuthToken) {
          handleFirestoreError(error, OperationType.GET, `organizations/${orgId}/settings/payroll`);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [orgId]);

  const handleSave = async () => {
    const localAuthToken = typeof window !== 'undefined'
      ? localStorage.getItem('app_auth_token')
      : null;

    setIsSaving(true);
    try {
      if (localAuthToken) {
        const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/settings`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localAuthToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ payroll: payrollToJson(settings) }),
        });
        if (!response.ok) {
          throw new Error(`Payroll settings save failed: ${response.status}`);
        }
      } else {
        await setDoc(doc(db, 'organizations', orgId, 'settings', 'payroll'), {
          ...settings,
          updatedAt: serverTimestamp()
        });
      }
      notify.confirm("Payroll settings saved successfully");
    } catch (error) {
      console.error("Error saving payroll settings:", error);
      notify.error("Failed to save settings");
      if (!localAuthToken) {
        handleFirestoreError(error, OperationType.WRITE, `organizations/${orgId}/settings/payroll`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const addDeduction = () => {
    setSettings({
      ...settings,
      deductions: [...settings.deductions, { name: '', percentage: 0 }]
    });
  };

  const removeDeduction = (index: number) => {
    const newDeductions = [...settings.deductions];
    newDeductions.splice(index, 1);
    setSettings({ ...settings, deductions: newDeductions });
  };

  const addAllowance = () => {
    setSettings({
      ...settings,
      allowances: [...settings.allowances, { name: '', percentage: 0 }]
    });
  };

  const removeAllowance = (index: number) => {
    const newAllowances = [...settings.allowances];
    newAllowances.splice(index, 1);
    setSettings({ ...settings, allowances: newAllowances });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 opacity-50">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p className="text-xl italic serif">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payroll Settings</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Configure your company payroll rules and defaults</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-8 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>Save Settings</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Company Info */}
        <div className="bg-white/50 border border-[#141414] p-8 rounded-[32px] space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-5 h-5 opacity-30" />
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Company Details</h3>
          </div>
          
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Legal Company Name</label>
            <input 
              type="text" 
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
              placeholder="e.g. Acme Corp"
              value={settings.companyName}
              onChange={(e) => setSettings({...settings, companyName: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Tax Identification Number</label>
            <input 
              type="text" 
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
              placeholder="e.g. 12-3456789"
              value={settings.taxNumber}
              onChange={(e) => setSettings({...settings, taxNumber: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Salary / Wages Account</label>
            <select
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
              value={settings.salaryAccount || ''}
              onChange={(e) => setSettings({...settings, salaryAccount: e.target.value})}
            >
              <option value="">Select Expense Account...</option>
              {accountsList.filter(a => a.type && a.type.toLowerCase().includes('expense')).map(account => (
                <option key={account.id} value={account.id}>
                  {account.code} - {account.name}
                </option>
              ))}
              {/* Fallback to show all accounts if filter is too strict */}
              {accountsList.filter(a => !a.type || !a.type.toLowerCase().includes('expense')).map(account => (
                <option key={account.id} value={account.id}>
                  {account.code} - {account.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payroll Payable / Liability Account</label>
            <select
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
              value={settings.liabilityAccount || ''}
              onChange={(e) => setSettings({...settings, liabilityAccount: e.target.value})}
            >
              <option value="">Select Liability Account...</option>
              {accountsList.filter(a => String(a.type).toLowerCase().includes('liability') || String(a.type).toLowerCase() === 'current liability').map(account => (
                <option key={account.id} value={account.id}>
                  {account.code} - {account.name}
                </option>
              ))}
              {/* Fallback */}
              {accountsList.filter(a => !(String(a.type).toLowerCase().includes('liability') || String(a.type).toLowerCase() === 'current liability')).map(account => (
                <option key={account.id} value={account.id}>
                  {account.code} - {account.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Payment Cycle */}
        <div className="bg-white/50 border border-[#141414] p-8 rounded-[32px] space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 opacity-30" />
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Payment Cycle</h3>
          </div>
          
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Pay Frequency</label>
            <select 
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
              value={settings.payFrequency}
              onChange={(e) => setSettings({...settings, payFrequency: e.target.value as any})}
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-weekly">Bi-weekly</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Default Pay Day (of month)</label>
            <input 
              type="number" 
              min="1"
              max="31"
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
              value={isNaN(settings.payDay) ? '' : (settings.payDay === 0 ? '' : settings.payDay)}
              onChange={(e) => setSettings({...settings, payDay: e.target.value === '' ? 0 : parseInt(e.target.value)})}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Currency</label>
            <input 
              type="text" 
              className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
              placeholder="e.g. USD, EGP, EUR"
              value={settings.currency}
              onChange={(e) => setSettings({...settings, currency: e.target.value.toUpperCase()})}
            />
          </div>
        </div>

        {/* Deductions */}
        <div className="bg-white/50 border border-[#141414] p-8 rounded-[32px] space-y-6 col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Percent className="w-5 h-5 opacity-30" />
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Standard Deductions</h3>
            </div>
            <button 
              onClick={addDeduction}
              className="flex items-center gap-2 text-xs font-bold hover:text-orange-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Deduction</span>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.deductions.map((deduction, index) => (
              <div key={index} className="flex gap-4 items-end bg-white/30 p-4 rounded-2xl border border-[#141414]/5">
                <div className="flex-1 space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Name</label>
                  <input 
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414]/10 p-3 rounded-xl outline-none focus:bg-white transition-all text-sm font-bold"
                    placeholder="e.g. Income Tax"
                    value={deduction.name}
                    onChange={(e) => {
                      const newDeductions = [...settings.deductions];
                      newDeductions[index].name = e.target.value;
                      setSettings({...settings, deductions: newDeductions});
                    }}
                  />
                </div>
                <div className="w-24 space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Rate (%)</label>
                  <input 
                    type="number" 
                    className="w-full bg-white/50 border border-[#141414]/10 p-3 rounded-xl outline-none focus:bg-white transition-all text-sm font-bold"
                    placeholder="0"
                    value={isNaN(deduction.percentage) ? '' : (deduction.percentage === 0 ? '' : deduction.percentage)}
                    onChange={(e) => {
                      const newDeductions = [...settings.deductions];
                      newDeductions[index].percentage = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      setSettings({...settings, deductions: newDeductions});
                    }}
                  />
                </div>
                <button 
                  onClick={() => removeDeduction(index)}
                  className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Allowances */}
        <div className="bg-white/50 border border-[#141414] p-8 rounded-[32px] space-y-6 col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Plus className="w-5 h-5 opacity-30" />
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Standard Allowances</h3>
            </div>
            <button 
              onClick={addAllowance}
              className="flex items-center gap-2 text-xs font-bold hover:text-orange-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Allowance</span>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.allowances.map((allowance, index) => (
              <div key={index} className="flex gap-4 items-end bg-white/30 p-4 rounded-2xl border border-[#141414]/5">
                <div className="flex-1 space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Name</label>
                  <input 
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414]/10 p-3 rounded-xl outline-none focus:bg-white transition-all text-sm font-bold"
                    placeholder="e.g. Housing"
                    value={allowance.name}
                    onChange={(e) => {
                      const newAllowances = [...settings.allowances];
                      newAllowances[index].name = e.target.value;
                      setSettings({...settings, allowances: newAllowances});
                    }}
                  />
                </div>
                <div className="w-24 space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Rate (%)</label>
                  <input 
                    type="number" 
                    className="w-full bg-white/50 border border-[#141414]/10 p-3 rounded-xl outline-none focus:bg-white transition-all text-sm font-bold"
                    placeholder="0"
                    value={isNaN(allowance.percentage) ? '' : (allowance.percentage === 0 ? '' : allowance.percentage)}
                    onChange={(e) => {
                      const newAllowances = [...settings.allowances];
                      newAllowances[index].percentage = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      setSettings({...settings, allowances: newAllowances});
                    }}
                  />
                </div>
                <button 
                  onClick={() => removeAllowance(index)}
                  className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
